/**
 * P6-282 round 2, item 2 -- wording SUPERSEDED by Jordan's own Claude Design
 * session (`D2D Watch v2.dc.html`) after the first draft of this file shipped
 * with invented copy for `watching`. Turns a review's stored `context` (the
 * bot's own `{status, progress}` snapshot -- `utils/watch_manager.py::
 * _review_context`) into the disclaimer LINE. The design shows this inside a
 * dashed note box headed "Didn't finish", with the returned line beneath it
 * in small uppercase mono -- **this function only returns the line**; the
 * box around it is V2-2's job, the first surface on the site that shows
 * review text at all (nothing does yet).
 *
 * Mirrors `cogs/watch/helpers.py::review_disclaimer` WORD FOR WORD --
 * CLAUDE.md gotcha 21: two implementations of one contract, asserted
 * against each other (`tests/test_watch_helpers.py`'s cross-check test,
 * which runs this exact file under `node`), never assumed to agree.
 * Change one, change both, and re-run that test.
 *
 * `STATUS_STOPPED` is declared locally rather than imported from
 * `./watchRequests.js` -- that module pulls in `react`, and this file has
 * to run standalone under plain `node` with no `node_modules` installed
 * (the same "zero dependencies" property `site/scripts/generate-content.mjs`
 * deliberately has, for the identical reason: `tests/test_command_docs.js`'s
 * own docstring). The value is the same string `TARGET_STOPPED` names there.
 */

const STATUS_STOPPED = 'stopped'

/**
 * "S2E4" or "E12" -- mirrors `cogs/watch/helpers.py::progress_label`'s own
 * episode formatting exactly (never `S0E12`, never the literal `SNoneE12`
 * for an anime, where `season` is a real `null` position, not "unset").
 * Not exported: `reviewDisclaimer`'s `progress` is always either `null` or
 * a complete `{season, episode}` pair (the bot's own
 * `_validate_review_context` guarantees `episode` is a real number
 * whenever `progress` is not `null`), so this never needs the "Not
 * started" branch `progress_label` itself has for a bare, positionless
 * call.
 */
function positionLabel(progress) {
  const { season, episode } = progress
  if (season === null || season === undefined) return `E${episode}`
  return `S${season}E${episode}`
}

/** The shared half of both this file's exports: "stopped at S2E4 of 62",
 * "stopped at E12 of 24" (an anime, no season), or "stopped at the start"
 * with no position at all. `progress` is `{season, episode}` or `null`;
 * `totalEpisodes` is the TITLE's own whole-series total (a live fact,
 * never frozen at a review's own `updated_at`), `null`/`undefined` when
 * the provider never reported one, which omits the "of N" clause rather
 * than guessing.
 *
 * Factored out in V2-4c so `reviewDisclaimer` (below, for an already-
 * SAVED review's stored context) and `finishPanelDnfLine` (for the
 * FinishPanel's own PROSPECTIVE note, before anything is saved) compute
 * the identical "where" fragment once -- CLAUDE.md gotcha 21, never two
 * hand-typed copies of the same recipe. Not exported: both callers below
 * are in this same file. */
function whereStopped(progress, totalEpisodes) {
  if (progress == null) return 'the start'
  const where = positionLabel(progress)
  return totalEpisodes !== null && totalEpisodes !== undefined ? `${where} of ${totalEpisodes}` : where
}

/**
 * `context` is `{status, progress}` or `null`/`undefined` (a review
 * written before this field existed, or one belonging to a finished
 * member with no disclaimer to show). `totalEpisodes` is the TITLE's own
 * whole-series total, passed in SEPARATELY from `context` -- it is a live
 * fact about the current title (a renewed show's total can grow), never a
 * value frozen at the review's own `updated_at` the way `progress` is.
 * `null`/`undefined` when the provider never reported one, which omits
 * the "of N" clause below rather than guessing.
 *
 * Returns the disclaimer LINE, or `null` when none applies. Built exactly
 * to the design's own recipe -- the apostrophe is U+2019 (’), the
 * separators are U+00B7 (·) with a space each side, never a plain `'` or
 * an em dash:
 *
 *   stopped, S2E4, total 62   -> "Didn’t finish · stopped at S2E4 of 62 · a review of part of it"
 *   stopped, S2E4, no total   -> "Didn’t finish · stopped at S2E4 · a review of part of it"
 *   stopped, no position      -> "Didn’t finish · stopped at the start · a review of part of it"
 *   stopped, anime E12 of 24  -> "Didn’t finish · stopped at E12 of 24 · a review of part of it"
 *   watching                  -> null (the design has no mid-watch label at all)
 *   finished, or no context   -> null
 */
export function reviewDisclaimer(context, totalEpisodes) {
  if (!context) return null
  if (context.status !== STATUS_STOPPED) return null
  return `Didn’t finish · stopped at ${whereStopped(context.progress, totalEpisodes)} · a review of part of it`
}

/**
 * V2-4c: the FinishPanel's own dnf note (`docs/design-briefs/watch/v2/
 * D2D Watch v2.dc.html`'s `fp()`, verbatim) -- NOT `reviewDisclaimer`
 * above, on purpose. That function labels an ALREADY-WRITTEN review that
 * someone else (or a past visit) is now reading; this one is a live,
 * PROSPECTIVE warning shown while the member is still typing, sitting
 * directly beneath the panel's own fixed heading (`Your review will be
 * marked "Didn't finish"`) -- so it never repeats "Didn't finish" as its
 * own clause the way the saved-review box does, and it never adds "a
 * review of part of it" (nothing has been written yet to describe).
 *
 * `status` is the member's OWN CURRENT status (`watchRequests.js`'s
 * `TARGET_STOPPED`), read live off the projection -- never a saved
 * review's stored `context.status`, because the FinishPanel can open
 * before any review exists at all. `null` when the member has not
 * stopped (nothing to warn about), matching `reviewDisclaimer`'s own
 * `null`-means-"no box" convention.
 *
 *   stopped, S2E4, total 62   -> "Didn’t finish · stopped at S2E4 of 62"
 *   stopped, no position      -> "Didn’t finish · stopped at the start"
 *   watching / watched        -> null
 */
export function finishPanelDnfLine(status, progress, totalEpisodes) {
  if (status !== STATUS_STOPPED) return null
  return `Didn’t finish · stopped at ${whereStopped(progress, totalEpisodes)}`
}
